import { useNavigate } from 'react-router-dom';

import { ProjectStatus } from '../types/project';

const STAGE_BIZZDESIGN_INTEGRATION = 6;

/**
 * ProjectStageRouter - Simple routing hook for live document navigation
 * Takes users directly to their current stage with no complex logic
 */
export const useProjectStageRouter = () => {
  const navigate = useNavigate();

  /**
   * Navigate to the user's current stage - simple and direct
   */
  const navigateToProjectStage = (
    projectId: number,
    projectStatus: ProjectStatus,
    _lastEditedSection?: string,
  ) => {
    // Simple mapping: take user to their current stage
    switch (projectStatus) {
      case ProjectStatus.CONTEXT_UPLOAD:
        navigate(`/project/${projectId}/context`);
        break;

      case ProjectStatus.CONTEXT_REVIEW:
        navigate(`/project/${projectId}/cra`);
        break;

      case ProjectStatus.ADR:
        navigate(`/project/${projectId}/adrs`);
        break;

      case ProjectStatus.GENERATION:
        navigate(`/project/${projectId}/generation`);
        break;

      case ProjectStatus.BIZZDESIGN_INTEGRATION:
        navigate(`/project/${projectId}/c4`);
        break;

      case ProjectStatus.IN_REVIEW:
        navigate(`/project/${projectId}/review`);
        break;

      case ProjectStatus.COMPLETED:
      case ProjectStatus.PUBLISHED:
        navigate(`/project/${projectId}/review`);
        break;

      default:
        navigate(`/project/${projectId}/setup`);
        break;
    }
  };

  /**
   * Navigate directly to any stage - no restrictions in live document approach
   */
  const navigateToStage = (projectId: number, stageId: number) => {
    const routes = [
      '', // placeholder for index 0
      `/project/${projectId}/setup`,        // Stage 1: Project Setup
      `/project/${projectId}/context`,      // Stage 2: Context Management
      `/project/${projectId}/cra`,          // Stage 3: Context Review
      `/project/${projectId}/adrs`,         // Stage 4: ADRs
      `/project/${projectId}/generation`,   // Stage 5: Architecture Generation
      `/project/${projectId}/c4`,           // Stage 6: Bizzdesign Integration / C4 Modeling
      `/project/${projectId}/review`,       // Stage 7: Final Review
    ];

    if (routes[stageId]) {
      navigate(routes[stageId]);
    }
  };

  /**
   * Get the current stage number based on project status
   */
  const getCurrentStage = (projectStatus: ProjectStatus): number => {
    switch (projectStatus) {
      case ProjectStatus.CONTEXT_UPLOAD:
        return 2;                    // Context Management
      case ProjectStatus.CONTEXT_REVIEW:
        return 3;                    // Context Review
      case ProjectStatus.ADR:
        return 4;                    // ADRs
      case ProjectStatus.GENERATION:
        return 5;                    // Architecture Generation
      case ProjectStatus.BIZZDESIGN_INTEGRATION:
        return STAGE_BIZZDESIGN_INTEGRATION;                    // Bizzdesign Integration
      case ProjectStatus.COMPLETED:
      case ProjectStatus.PUBLISHED:
        return 7;                    // Final Review
      default:
        return 1;
    }
  };

  return {
    navigateToProjectStage,
    navigateToStage,
    getCurrentStage,
  };
};

/**
 * Hook to get the correct route for a project based on its status
 */
export const useProjectRoute = (
  projectId: number,
  projectStatus: ProjectStatus,
  _lastEditedSection?: string,
): string => {
  switch (projectStatus) {
    case ProjectStatus.CONTEXT_UPLOAD:
      return `/project/${projectId}/context`;
    case ProjectStatus.CONTEXT_REVIEW:
      return `/project/${projectId}/cra`;
    case ProjectStatus.ADR:
      return `/project/${projectId}/adrs`;
    case ProjectStatus.GENERATION:
      return `/project/${projectId}/generation`;
    case ProjectStatus.BIZZDESIGN_INTEGRATION:
      return `/project/${projectId}/c4`;
    case ProjectStatus.COMPLETED:
    case ProjectStatus.PUBLISHED:
      return `/project/${projectId}/review`;
    default:
      return `/project/${projectId}/setup`;
  }
};
